"use client";

import { c as _c } from "react/compiler-runtime";
import React from "react";
import type { Channel } from "@/payload-types";
type LogoSize = "sm" | "md" | "lg";
interface Props {
  channel: Channel;
  /** Preset size tier. `sm` = inline rows, `md` = default, `lg` = modal cards. */
  size?: LogoSize;
  /** Escape hatch for custom sizing. Overrides `size`. */
  sizeClassName?: string;
  className?: string;
}
const SIZE_PRESETS: Record<LogoSize, {
  imgHeight: string;
  pillHeight: string;
  pillText: string;
}> = {
  sm: {
    imgHeight: "h-6",
    pillHeight: "h-6",
    pillText: "text-xs"
  },
  md: {
    imgHeight: "h-8",
    pillHeight: "h-8",
    pillText: "text-xs"
  },
  lg: {
    imgHeight: "h-10",
    pillHeight: "h-10",
    pillText: "text-sm"
  }
};

/**
 * Renders a broadcaster logo inside a fixed-size container so the visual
 * footprint stays uniform regardless of the logo's native aspect ratio
 * (square icon vs. wide wordmark). The image itself uses `object-contain`
 * and is capped at full container width/height.
 *
 * Falls back to a text pill at the same height when no logo is uploaded.
 */
export function BroadcasterLogo(t0) {
  const $ = _c(13);
  const {
    channel,
    size: t1,
    sizeClassName,
    className
  } = t0;
  const size = t1 === undefined ? "md" : t1;
  const preset = SIZE_PRESETS[size];
  const logo = typeof channel.logo === "object" ? channel.logo : null;
  const logoUrl = logo && "url" in logo ? logo.url as string | null | undefined : null;
  if (logoUrl) {
    const t2 = channel.name;
    const t3 = sizeClassName ?? `${preset.imgHeight} w-auto`;
    let t4;
    if ($[0] !== className || $[1] !== t3) {
      t4 = ["object-contain shrink-0", t3, className].filter(Boolean);
      $[0] = className;
      $[1] = t3;
      $[2] = t4;
    } else {
      t4 = $[2];
    }
    const t5 = t4.join(" ");
    let t6;
    if ($[3] !== channel.name || $[4] !== logoUrl || $[5] !== t5) {
      t6 = <img src={logoUrl} alt={t2} className={t5} />;
      $[3] = channel.name;
      $[4] = logoUrl;
      $[5] = t5;
      $[6] = t6;
    } else {
      t6 = $[6];
    }
    return t6;
  }
  const t2 = sizeClassName ?? `${preset.pillHeight} ${preset.pillText}`;
  let t3;
  if ($[7] !== className || $[8] !== t2) {
    t3 = ["inline-flex items-center justify-center shrink-0", "px-2 rounded-full", "font-semibold whitespace-nowrap", "bg-muted text-foreground", t2, className].filter(Boolean);
    $[7] = className;
    $[8] = t2;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  const t4 = t3.join(" ");
  let t5;
  if ($[10] !== channel.name || $[11] !== t4) {
    t5 = <span className={t4}>{channel.name}</span>;
    $[10] = channel.name;
    $[11] = t4;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  return t5;
}